/**
 * =========================================================================================
 * Subscription Timeline Modal
 * =========================================================================================
 *
 * Uses getActivityTimeline socket event to show a specific subscription's
 * complete activity history in a modal.
 *
 * Fires: billing|cashfree|subscription|getActivityTimeline
 * Required param: subscriptionId
 * Optional: category (null/USER_ACTION/SYSTEM_EVENT), limit, skip
 *
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';
import UModal from 'ubs-framework/v2/UModal/NewUModal';
import UTags from 'ubs-framework/v2/UTags';
import UCustomScrollBar from 'ubs-framework/v2/UCustomScrollBar';
import { showToastError, numberWithComaToFixed, getKeyName } from 'ubs-framework/v2/common/function';
import { dateFormatter } from 'ubs-framework/v2/common/function';
import { sendData } from '../../../../../store/actions';
import useSocket from '../../../../common/useSocket';
import ActivityCard from '../../activity/components/activity-card';
import { SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS_OPTIONS } from '../../../../../common/constant';

const CATEGORY_TABS = [
    { label: 'All', value: null },
    { label: 'My Actions', value: 'USER_ACTION' },
    { label: 'System Events', value: 'SYSTEM_EVENT' },
];

const STATUS_COLORS = {
    ACTIVE: '#60B158',
    PAUSED: '#FFA700',
    CANCELLED: '#FF6961',
    INITIALIZED: '#999999',
    ON_HOLD: '#FF6961',
    EXPIRED: '#999999',
};

const SkeletonRow = () => (
    <div className="u_flex u_mb-16">
        <div className="u_flex u_direction-column u_align-center u_mr-16">
            <div className="u_h-36 u_w-50 u_radius-8 u_bg-thead" />
            <div className="u_flex-1 u_w-1 u_bg-lightgray u_my-4" />
        </div>
        <div className="u_flex-1-0 u_pb-4">
            <div className="u_bg-white u_radius-8 u_b-light u_p-16">
                <div className="u_h-14 u_w-50 u_bg-thead u_radius-5 u_mb-10" />
                <div className="u_h-12 u_w-full u_bg-thead u_radius-5 u_mb-8" />
                <div className="u_flex-align">
                    <div className="u_h-20 u_w-50 u_bg-thead u_radius-5 u_mr-8" />
                    <div className="u_h-20 u_w-50 u_bg-thead u_radius-5" />
                </div>
            </div>
        </div>
    </div>
);

const SubscriptionTimelineModal = ({ open, onClose, subscriptionId, subscriptionName }) => {
    const dispatch = useDispatch();
    const [activities, setActivities] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [category, setCategory] = useState(null);

    const prevCategoryRef = useRef(null);
    const pendingSkipRef = useRef(0);

    const getData = useCallback((isLoadMore = false) => {
        if (!subscriptionId) return;

        if (isLoadMore) {
            setLoadingMore(true);
        } else {
            setDataLoading(true);
        }

        const skip = isLoadMore ? activities.length : 0;
        pendingSkipRef.current = skip;

        const payload = {
            subscriptionId,
            limit: 20,
            skip,
            ...(category && { category }),
        };

        sendData(dispatch, 'billing|cashfree|subscription|getActivityTimeline', payload);
    }, [subscriptionId, category, activities.length]);

    const { off, refresh } = useSocket(res => {
        if (!res.error) {
            switch (res.event) {
                case 'billing|cashfree|subscription|getActivityTimeline': {
                    const newActivities = res?.data?.activities || [];
                    const pagination = res?.data?.pagination || {};
                    const responseSkip = Number(pagination?.skip ?? pendingSkipRef.current ?? 0);
                    const isLoadMoreResponse = responseSkip > 0;

                    if (res?.data?.subscription) {
                        setSubscription(res.data.subscription);
                    }

                    if (isLoadMoreResponse) {
                        setActivities(prev => [...prev, ...newActivities]);
                    } else {
                        setActivities(newActivities);
                    }
                    setLoadingMore(false);
                    setDataLoading(false);

                    setTotalRecords(pagination?.total || 0);
                    setHasMore(pagination?.hasMore || false);
                    break;
                }
                default:
                    return true;
            }
        } else {
            setDataLoading(false);
            setLoadingMore(false);
            showToastError(res?.data?.error || 'Failed to load timeline.');
        }
    });

    useEffect(() => {
        if (open && subscriptionId) {
            refresh();
            getData(false);
        }
        return () => off();
    }, [open, subscriptionId]);

    useEffect(() => {
        if (open && prevCategoryRef.current !== category) {
            setActivities([]);
            prevCategoryRef.current = category;
            refresh();
            getData(false);
        }
    }, [category]);

    const handleLoadMore = () => {
        getData(true);
    };

    const handleClose = () => {
        setActivities([]);
        setSubscription(null);
        setTotalRecords(0);
        setHasMore(false);
        setCategory(null);
        onClose();
    };

    // Group activities by date
    const getGroupedActivities = () => {
        if (!activities || activities.length === 0) return {};
        const groups = {};
        const moment = require('moment');

        activities.forEach(activity => {
            const date = moment(activity?.timestamp || activity?.createdAt);
            let label;
            if (date.isSame(moment(), 'day')) label = 'Today';
            else if (date.isSame(moment().subtract(1, 'day'), 'day')) label = 'Yesterday';
            else if (date.isSame(moment(), 'year')) label = date.format('DD MMM');
            else label = date.format('DD MMM YYYY');

            if (!groups[label]) groups[label] = [];
            groups[label].push(activity);
        });
        return groups;
    };

    const grouped = getGroupedActivities();
    const dateKeys = Object.keys(grouped);

    return (
        <UModal
            open={open}
            onClose={handleClose}
            header={<div className='u_flex-align'>
                <h4>Current Subscription Timeline</h4>
                {subscription && (<div className="u_flex-align u_pl-8">
                        <span className="u_small-font u_fw-600 u_color-title u_mr-8">
                           ({subscriptionName || subscription?.subscriptionId})
                        </span>
                        <UTags
                            className="u_ib"
                            title={getKeyName(SUBSCRIPTION_STATUS_OPTIONS, subscription?.status)?.label}
                            color={getKeyName(SUBSCRIPTION_STATUS_OPTIONS, subscription?.status)?.color || '#999999'}
                        />
                </div>)}
            </div>}
            maxWidth={800}
        >
            <div className="u_p-0">
                {/* Subscription info bar */}


                {/* Category filter tabs */}
                <div className="u_flex-align u_px-20 u_py-12 u_bb-light">
                    <div className="u_flex-align u_bg-thead u_radius-8 u_p-4">
                        {CATEGORY_TABS.map(tab => {
                            const isActive = category === tab.value;
                            return (
                                <div
                                    key={tab.label}
                                    className={`u_px-14 u_py-6 u_radius-5 u_cp u_small-font u_fw-500 u_ws-nowrap ${isActive
                                        ? 'u_bg-white u_color-blue u_b-light'
                                        : 'u_color-font'
                                        }`}
                                    onClick={() => setCategory(tab.value)}
                                >
                                    {tab.label}
                                </div>
                            );
                        })}
                    </div>
                    {totalRecords > 0 && (
                        <span className="u_small-font u_color-font u_ml-12">
                            {activities.length} of {totalRecords}
                        </span>
                    )}
                </div>

                {/* Timeline content */}
                {/* <div className="u_overflow-auto" >
                    <UCustomScrollBar maxHeight={460}> */}
                {/* Loading skeleton */}
                {dataLoading && activities.length === 0 && (
                    <div className="u_px-20 u_pt-16">
                        {[1, 2, 3].map(i => <SkeletonRow key={i} />)}
                    </div>
                )}

                {/* Empty state */}
                {!dataLoading && activities.length === 0 && (
                    <div className="u_flex u_direction-column u_align-center u_content-center u_py-30 u_px-20">
                        <div className="u_bg-thead u_radius-8 u_p-14 u_mb-14">
                            <Icon.Clock size={28} fill={COLOR.u_icon_bg} />
                        </div>
                        <p className="u_font-14 u_fw-600 u_color-title u_mb-6">No Activities Found</p>
                        <p className="u_small-font u_color-font u_text-center">
                            {category
                                ? `No ${category === 'USER_ACTION' ? 'user action' : 'system event'} activities for this subscription.`
                                : 'No activity history available for this subscription yet.'
                            }
                        </p>
                    </div>
                )}

                {/* Grouped activities */}
                {activities.length > 0 && (
                    <div className="u_px-20 u_pt-16">
                        {dateKeys?.map(dateLabel => {
                            const group = grouped[dateLabel];
                            return (
                                <div key={dateLabel} className="u_mb-8">
                                    {/* Date header */}
                                    <div class="u_flex-align u_mb-10">
                                        <div class="u_h-1 u_flex-1-0 u_bg-blue-2" />
                                        <div class="u_flex-align u_bg-lightblue u_radius-5 u_px-10 u_py-4 u_mx-8">
                                            <span class="u_small-font u_fw-600 u_color-blue u_ws-nowrap">{dateLabel}</span>
                                        </div>
                                        <div class="u_h-1 u_flex-1-0 u_bg-blue-2" />
                                    </div>

                                    {group?.map((activity, idx) => {
                                        const isLast = dateLabel === dateKeys[dateKeys.length - 1] && idx === group.length - 1 && !hasMore;
                                        return (
                                            <ActivityCard
                                                key={activity?._id || idx}
                                                activity={activity}
                                                isLast={isLast}
                                            />
                                        );
                                    })}
                                </div>
                            );
                        })}

                        {/* Load more */}
                        {hasMore && (
                            <div className="u_flex u_content-center u_mt-4 u_mb-16">
                                <div
                                    className={`u_flex-align u_content-center u_px-20 u_py-8 u_radius-8 u_b-light u_bg-white u_cp u_small-font u_fw-500 u_color-blue ${loadingMore ? 'u_disabled' : ''}`}
                                    onClick={() => !loadingMore && handleLoadMore()}
                                >
                                    {loadingMore ? (
                                        <React.Fragment>
                                            <Icon.Preloader /> <span className="u_ml-8">Loading...</span>
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment>
                                            <Icon.DownArrow size={3} fill={COLOR.u_blue} />
                                            <span className="u_ml-8">Load More</span>
                                        </React.Fragment>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* </UCustomScrollBar>
                </div> */}
            </div>
        </UModal>
    );
};

export default SubscriptionTimelineModal;
